import { useState } from 'react';
import { useGame } from '../state/GameContext';
import { useGameActions } from '../hooks/useGameActions';
import BuyMeACoffee from '../components/BuyMeACoffee';

export default function ResultsScreen() {
  const { state } = useGame();
  const { playAgain, resetGame } = useGameActions();
  const [shareStatus, setShareStatus] = useState('');

  if (!state.finalLeaderboard) return null;

  const currentPlayerIndex = state.finalLeaderboard.findIndex(
    (player) => player.id === state.playerId,
  );
  const currentPlayer = state.finalLeaderboard[currentPlayerIndex];
  const placement = currentPlayerIndex === 0
    ? '1st'
    : currentPlayerIndex === 1
      ? '2nd'
      : currentPlayerIndex === 2
        ? '3rd'
        : `#${currentPlayerIndex + 1}`;

  const handleShareResult = async () => {
    if (!currentPlayer) return;

    // Clear any status from a previous attempt so a stale 'Thanks for sharing!'
    // cannot outlive a later cancellation.
    setShareStatus('');

    const resultText = `I scored ${currentPlayer.score} pts and placed ${placement} in Photo Blender. Can you beat me? ${window.location.origin}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Photo Blender',
          text: resultText,
          url: window.location.origin,
        });
        setShareStatus('Thanks for sharing!');
        return;
      }

      await navigator.clipboard.writeText(resultText);
      setShareStatus('Result copied!');
    } catch (error) {
      // Dismissing the native share sheet rejects with AbortError. That is a
      // deliberate choice by the player, not a failure worth reporting.
      if ((error as DOMException)?.name === 'AbortError') return;
      setShareStatus('Could not share your result. Please try again.');
    }
  };

  return (
    <div className="text-center">
      <h2>Game Finished!</h2>

      <div className="leaderboard">
        <h3>Final Results</h3>
        {state.finalLeaderboard.map((player, index) => {
          const isCurrentPlayer = player.id === state.playerId;
          const rankClass = index < 3 ? `rank-${index + 1}` : '';
          return (
            <div
              key={index}
              className={`leaderboard-item ${rankClass}`}
              style={isCurrentPlayer ? {
                background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.25) 0%, rgba(126, 34, 206, 0.2) 100%)',
                border: '3px solid rgba(147, 51, 234, 0.6)',
                boxShadow: '0 0 20px rgba(147, 51, 234, 0.4)',
              } : {}}
            >
              <span className="rank-badge">
                {index === 0 ? '1st' : index === 1 ? '2nd' : index === 2 ? '3rd' : `#${index + 1}`}
              </span>
              <span className="player-name">
                {player.name}
                {isCurrentPlayer && (
                  <span className="ml-2 text-xs bg-purple-500/40 px-1.5 py-0.5 rounded-full font-bold">YOU</span>
                )}
                {player.streak && player.streak >= 3 && (
                  <span className="ml-2 text-xs bg-orange-500/30 px-1.5 py-0.5 rounded-full">
                    {player.streak} streak
                  </span>
                )}
              </span>
              <span className="player-score">{player.score} pts</span>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-col gap-3 items-center">
        {currentPlayer && (
          <>
            <button
              className="button button-primary"
              onClick={handleShareResult}
            >
              Share my score
            </button>
            {shareStatus && (
              <p className="text-sm text-white/80" role="status">
                {shareStatus}
              </p>
            )}
          </>
        )}
        {state.isHost ? (
          <button
            className="button button-primary"
            onClick={playAgain}
          >
            Play Again
          </button>
        ) : (
          <div className="p-3 bg-white/8 rounded-xl text-center text-sm opacity-90">
            Waiting for host to start a new game...
          </div>
        )}
        <button
          className="button button-secondary"
          onClick={resetGame}
        >
          Leave Game
        </button>

        <div className="mt-4 p-4 bg-white/5 rounded-xl border-2 border-white/10 text-center">
          <p className="mb-3 text-sm text-white/80">
            Enjoyed the game?
          </p>
          <BuyMeACoffee size="medium" text="Buy Me a Coffee" />
        </div>
      </div>
    </div>
  );
}
